import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  getReferralStatsServer,
  type ReferralStatsResponse,
  type ReferredUser,
} from "@/server/referrals";
import { toast } from "sonner";
import { CustomPagination } from "@/components/ui/custom-pagination";
import { NoData } from "@/components/ui/no-data";
import { cn } from "@/lib/utils";

import HeaderName from "@/assets/images/referrals/header-name.svg";
import HeaderStatus from "@/assets/images/referrals/header-status.svg";
import HeaderCredits from "@/assets/images/referrals/header-credits.svg";
import HeaderCreated from "@/assets/images/referrals/header-created.svg";
import MetricRegistered from "@/assets/images/referrals/registered-users.svg";
import MetricActivated from "@/assets/images/referrals/activated-users.svg";
import MetricPaid from "@/assets/images/referrals/paid-users.svg";
import MetricCredits from "@/assets/images/referrals/credits-earned.svg";
import IconCopy from "@/assets/icons/copy.svg";
import { MetricPill } from "@/components/referrals/metric-pill";

export const Route = createFileRoute(undefined)({
  // 加载推荐统计数据（BFF 服务端函数）
  loader: async (): Promise<ReferralStatsResponse> => {
    const stats = await getReferralStatsServer();
    return stats;
  },
  component: ReferralsPage,
});

function ReferralsPage() {
  const { t } = useTranslation(["settings"]);
  const initial = Route.useLoaderData() as ReferralStatsResponse;
  // 生成推荐链接：优先使用 window.location.origin，如果在服务端则回退到 localhost
  // Generate referral link: prefer window.location.origin, fallback to localhost if on server
  const referralLink = useMemo<string>(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost:3000";
    try {
      return new URL(`/signin?invite=${initial.referral_code}`, origin).toString();
    } catch {
      return `${origin}/signin?invite=${initial.referral_code}`;
    }
  }, [initial.referral_code]);

  const users = useMemo<ReferredUser[]>(
    () => initial.referred_users || [],
    [initial.referred_users],
  );
  // 分页状态管理
  // Pagination state management
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // 计算总页数和当前页显示的用户数据
  // Calculate total pages and displayed users for current page
  const totalPages = Math.max(1, Math.ceil(users.length / pageSize));
  const displayedUsers = useMemo(
    () => users.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize),
    [users, page, pageSize],
  );

  // 顶部指标计算：注册用户、激活用户、付费用户、获得积分
  // Metrics calculation: Registered, Activated, Paid, Credits Earned
  const metrics = useMemo(() => {
    const registered = users.length;
    // 激活用户：状态为 "activated" (通常指创建了 Campaign)
    const activated = users.filter((u) => String(u.status).toLowerCase() === "activated").length;
    // 付费用户：状态为 "paid" (完成了首次支付)
    const paid = users.filter((u) => String(u.status).toLowerCase() === "paid").length;
    const credits = initial.deal_credits ?? 0;
    return { registered, activated, paid, credits };
  }, [users, initial.deal_credits]);

  // 复制推荐链接到剪贴板
  // Copy referral link to clipboard
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      toast.success(t("workspace.referralsPage.copied"));
    } catch {
      toast.error(t("workspace.referralsPage.copyFailed"));
    }
  };

  // 渲染状态徽标：根据不同状态显示不同颜色
  // Render status badge: display different colors based on status
  // Paid -> Green, Activated -> Orange, Registered -> Purple
  const statusBadge = (status: string) => {
    const s = String(status).toLowerCase();
    const color =
      s === "paid" ? "bg-[#54AB89]" : s === "activated" ? "bg-[#E18000]" : "bg-[#CE67DB]";
    const label =
      s === "paid"
        ? t("workspace.referralsPage.status.paid")
        : s === "activated"
          ? t("workspace.referralsPage.status.activated")
          : t("workspace.referralsPage.status.registered");
    return (
      <div className="inline-flex items-center gap-2">
        <span className={`inline-block size-2 rounded-full ${color}`} />
        <span className="font-['PingFang_MO'] text-[14px] font-normal leading-none capitalize text-[#7E7E7E]">
          {label}
        </span>
      </div>
    );
  };

  const generalTextClass =
    "font-['PingFang_MO'] text-[14px] font-normal leading-none capitalize text-[#7E7E7E]";

  return (
    <div className="h-full flex flex-col">
      <div className="pl-6 pr-6 pt-[24px] pb-[6px] font-['PingFang_MO'] font-medium text-[14px] leading-none tracking-normal text-black">
        {t("workspace.referralsPage.title")}
      </div>
      <div className="pl-6 pr-6 font-['Inter'] font-normal text-[12px] leading-none tracking-normal text-[#8B8B8B]">
        {t("workspace.referralsPage.subtitle")}
      </div>

      <div className="ml-6 mr-6  bg-white  pt-[24px]">
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-[#F6F7F8] px-[14px] py-[6px] min-h-[32px] flex items-center text-justify font-['PingFang_SC'] text-[14px] font-normal leading-none text-[#7E7E7E] rounded-[12px] box-border">
            {referralLink}
          </div>
          <Button
            variant="outline"
            onClick={copyLink}
            className="gap-2 h-[32px] w-[80px] py-0 rounded-[12px] justify-center"
          >
            <img src={IconCopy} alt="" className="size-[14px]" />
            {t("workspace.referralsPage.copy")}
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[15px] mt-[24px] mb-[16px]">
          <MetricPill
            icon={<img src={MetricRegistered} alt="" className="size-5" />}
            label={t("workspace.referralsPage.metrics.registered")}
            value={metrics.registered}
          />
          <MetricPill
            icon={<img src={MetricActivated} alt="" className="size-5" />}
            label={t("workspace.referralsPage.metrics.activated")}
            value={metrics.activated}
          />
          <MetricPill
            icon={<img src={MetricPaid} alt="" className="size-5" />}
            label={t("workspace.referralsPage.metrics.paidUsers")}
            value={metrics.paid}
          />
          <MetricPill
            icon={<img src={MetricCredits} alt="" className="size-5" />}
            label={t("workspace.referralsPage.metrics.creditsEarned")}
            value={metrics.credits}
          />
        </div>
      </div>

      {/* 内容区高度自适应数据，不再强制撑满屏幕 */}
      <div className={cn("ml-6 mr-6  pb-4 flex flex-col flex-1 overflow-hidden justify-between")}>
        <div className="rounded-xl border border-[#DFDFDF] bg-white flex flex-col shrink min-h-0 overflow-hidden">
          <div className="overflow-auto flex-1" style={{ scrollbarGutter: "stable" }}>
            <Table className="table-fixed">
              <TableHeader className="sticky top-0 z-10 bg-[#f6f6f6]">
                <TableRow>
                  <TableHead className="w-[35%]">
                    <span className="inline-flex items-center gap-1">
                      <img src={HeaderName} alt="" className="size-5" />
                      {t("workspace.referralsPage.table.name")}
                    </span>
                  </TableHead>
                  <TableHead className="w-[20%]">
                    <span className="inline-flex items-center gap-1">
                      <img src={HeaderStatus} alt="" className="size-5" />
                      {t("workspace.referralsPage.table.status")}
                    </span>
                  </TableHead>
                  <TableHead className="w-[20%]">
                    <span className="inline-flex items-center gap-1">
                      <img src={HeaderCredits} alt="" className="size-5" />
                      {t("workspace.referralsPage.table.creditsEarned")}
                    </span>
                  </TableHead>
                  <TableHead className="w-[25%]">
                    <span className="inline-flex items-center gap-1">
                      <img src={HeaderCreated} alt="" className="size-5" />
                      {t("workspace.referralsPage.table.created")}
                    </span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedUsers.map((u, idx) => (
                  <TableRow key={`${u.name}-${idx}`}>
                    <TableCell>
                      <div className="flex items-center gap-2 min-w-0">
                        {u.avatar_url ? (
                          <img
                            src={u.avatar_url}
                            alt={u.name}
                            className="size-6 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="size-6 rounded-full bg-[#f4f4f4] flex-shrink-0" />
                        )}
                        <span
                          className="font-['PingFang_SC'] text-[16px] font-medium leading-none text-black truncate"
                          title={u.name}
                        >
                          {u.name}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{statusBadge(u.status)}</TableCell>
                    <TableCell>
                      {u.reward_issued ? (
                        <span className={generalTextClass}>+ 1 Deal</span>
                      ) : (
                        <span className={generalTextClass}>—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={generalTextClass}>{formatDate(u.joined_at)}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {displayedUsers.length === 0 && (
              <div className="flex items-center justify-center py-12">
                <NoData
                  title={t("workspace.referralsPage.empty.title")}
                  description={t("workspace.referralsPage.empty.description")}
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end pt-4">
          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={users.length}
            pageSize={pageSize}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(1);
            }}
          />
        </div>
      </div>
    </div>
  );
}

function formatDate(s?: string) {
  if (!s) return "—";
  try {
    const dt = new Date(s);
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    const hh = String(dt.getHours()).padStart(2, "0");
    const mi = String(dt.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
  } catch {
    return s;
  }
}
